import { inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { API_BASE_URL } from '@app/core/tokens/api-base-url.token';
import { SnackbarService } from '@app/core/services/snackbar.service';
import {
  AddDamageToPackageRequest,
  DamageInPackageModel,
  RemoveDamagesFromPackageBulkRequest,
} from '@app/core/models/product-parameters/damage-in-package.model';

@Injectable({
  providedIn: 'root',
})
export class DamageInPackageService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseUrl = inject(API_BASE_URL);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);

  readonly isLoading = signal(false);

  private get baseUrl(): string {
    return `${this.apiBaseUrl}/b2b/damage-in-package`;
  }

  getByPackageId(damagePackageId: string): Observable<DamageInPackageModel[]> {
    if (!damagePackageId) return of([]);
    this.isLoading.set(true);
    return this.http
      .get<DamageInPackageModel[]>(`${this.baseUrl}/by-package/${damagePackageId}`)
      .pipe(
        catchError(error => {
          console.error('Ошибка при получении типов повреждений пакета:', error);
          this.translate
            .get('Ошибка при получении типов повреждений пакета')
            .subscribe(msg => this.snackbarService.error(msg));
          return of([]);
        }),
        finalize(() => this.isLoading.set(false))
      );
  }

  add(body: AddDamageToPackageRequest): Observable<DamageInPackageModel> {
    this.isLoading.set(true);
    return this.http.post<DamageInPackageModel>(this.baseUrl, body).pipe(
      catchError(error => {
        console.error('Ошибка при добавлении типа повреждения в пакет:', error);
        this.translate
          .get('Ошибка при добавлении типа повреждения в пакет')
          .subscribe(msg => this.snackbarService.error(msg));
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  remove(damagePackageId: string, typeOfDamageId: string): Observable<void> {
    this.isLoading.set(true);
    const params = new HttpParams()
      .set('damagePackageId', damagePackageId)
      .set('typeOfDamageId', typeOfDamageId);
    return this.http.delete<void>(this.baseUrl, { params }).pipe(
      catchError(error => {
        console.error('Ошибка при удалении типа повреждения из пакета:', error);
        this.translate
          .get('Ошибка при удалении типа повреждения из пакета')
          .subscribe(msg => this.snackbarService.error(msg));
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }

  removeBulk(body: RemoveDamagesFromPackageBulkRequest): Observable<void> {
    this.isLoading.set(true);
    return this.http.delete<void>(`${this.baseUrl}/bulk`, { body }).pipe(
      catchError(error => {
        console.error('Ошибка при массовом удалении типов повреждений из пакета:', error);
        this.translate
          .get('Ошибка при удалении типа повреждения из пакета')
          .subscribe(msg => this.snackbarService.error(msg));
        throw error;
      }),
      finalize(() => this.isLoading.set(false))
    );
  }
}
